import DOMPurify from 'dompurify';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommentElementProps } from '../../comments/comment-element.component';
import CommentsList from '../../comments/comments-list.component';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import type { DynamicFieldComponentProps } from '../../dynamic-form/dynamic-form.interfaces';
import EmptyFieldLabel from '../../labels/empty-field-label';
import FieldLabel from '../../labels/field-label';
import { RichTextArea } from '../../rich-text/rich-text-area';

export interface HistoryFieldProps extends DynamicFieldComponentProps<CommentElementProps[]> {
  imageSrc?: string;
  fullName?: string;
  formatDate?: (date: Date) => string;
}

const removeAllHTMLTags = (value: string): string => {
  return DOMPurify.sanitize(value, { ALLOWED_TAGS: ['#text', 'img'] });
};

export default function HistoryField({
  label,
  value,
  readOnly,
  required,
  onChange,
  error,
  labelStyles,
  fullName,
  formatDate,
  placeholder,
}: HistoryFieldProps) {
  const { t } = useTranslation();

  const commentsRef = useRef<CommentElementProps[]>([]);

  const comments: Array<CommentElementProps> = Array.isArray(value) ? value : [];
  commentsRef.current = comments.filter((comment) => comment.draft !== true);
  const _draftMessage = comments.find((comment) => comment.draft === true)?.message || '';

  const handleChange = (value?: string) => {
    // Handler uses commentsRef to avoid passing stale values when doing updates
    if (onChange && typeof onChange === 'function' && value) {
      let newValue;
      if (removeAllHTMLTags(value).length === 0) {
        newValue = [...commentsRef.current];
      } else {
        newValue = [
          {
            message: value,
            draft: true,
            date: new Date().toISOString(),
            user: fullName || t('user'),
          },
          ...commentsRef.current,
        ];
      }
      onChange?.(newValue as CommentElementProps[]);
    }
  };

  return (
    <>
      {label && (
        <div className="flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {readOnly && commentsRef.current.length === 0 && <EmptyFieldLabel />}
      {!readOnly && (
        <RichTextArea content={_draftMessage} onChange={handleChange} placeholder={placeholder} error={error} />
      )}
      {commentsRef.current.length > 0 && (
        <CommentsList comments={commentsRef.current} readOnly={readOnly} formatDate={formatDate} />
      )}
    </>
  );
}
